import { DataViewVisualizationKey, isViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import { CHART_BUNDLE_NAMES, MAP_BUNDLE_NAMES, TABLE_BUNDLE_NAMES } from 'cadenza/workbook/model/attribute-bundle-name';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { DataView } from 'cadenza/workbook/data-view/data-view';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeBundleDefinitionDto } from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import { getAttributeById, getDataViewById } from 'cadenza/workbook/workbook.state';
import { getStore } from 'cadenza/store/cadenza-store';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { entries } from 'cadenza/utils/type-util';

export const UNSET_OBJECTTYPE_ID = 'VOID/VOID';

/** Attribute count and limits for one attribute bundle */
export interface BundleAttributeCountAndLimits {
  /** The current number of attributes defined in the bundle*/
  current: number;
  /** The minimum number of attributes required in the bundle*/
  min: number;
  /** The maximum number of attributes allowed in the bundle*/
  max: number;
}

/**
 * @see net/disy/cadenza/workbook/configuration/AttributeBundleDefinitionLimitsSpecifics.java
 */
export interface AttributeBundleDefinitionLimitsSpecifics {
  olapTableMaxRowDimensionsLimit: number;
  olapTableMaxMeasuresLimit: number;
  pivotTableMaxRowDimensionsLimit: number;
  pivotTableMaxColumnDimensionsLimit: number;
  pivotTableMaxMeasuresLimit: number;
  defaultMaxMeasuresLimit: number;
}

export function isColumnAttribute(attribute: WorkbookAttributeDto, dataView: DataView) {
  if (isPivotTable(dataView)) {
    const columnAttributes = dataView.getAttributes(TABLE_BUNDLE_NAMES.COLUMNS);
    if (columnAttributes.find((attr) => attr.id === attribute.id)) {
      return true;
    }
  }
}

export function isPivotTable(dataView: DataView) {
  return (
    isTable(dataView) &&
    dataView.hasAttributeBundle(TABLE_BUNDLE_NAMES.COLUMNS) &&
    dataView.getAttributes(TABLE_BUNDLE_NAMES.COLUMNS).length > 0
  );
}

export function isTable(dataView: DataView) {
  return isViewType(dataView.visualizationKey, ViewType.TABLE);
}

export function isIndicator(dataView: DataView) {
  return dataView.visualizationKey === 'OTHER.INDICATOR';
}

// Only for charts with only one attribute
export function getChartMeasureAttribute(dataView: DataView) {
  const { MEASURE } = CHART_BUNDLE_NAMES;
  const allAttributeBundles = dataView.getAllAttributeBundles();
  if (allAttributeBundles[MEASURE]?.length === 1) {
    return allAttributeBundles[MEASURE][0];
  }
}

// Chart that doesn't have a dimension attribute but multiple measures instead
export function isMultiMeasureSimpleChart(dataView: DataView) {
  const visualizationType = dataView.visualizationKey;
  const allAttributeBundles = dataView.getAllAttributeBundles();
  const { COLUMN_CHART, BAR_CHART, LINE_CHART } = DataViewVisualizationKey[ViewType.CHART];
  const { MEASURE, DIMENSION } = CHART_BUNDLE_NAMES;
  const chartTypes = [COLUMN_CHART, BAR_CHART, LINE_CHART];
  if (chartTypes.includes(visualizationType)) {
    if (!allAttributeBundles[DIMENSION]?.length && allAttributeBundles[MEASURE].length > 1) {
      return true;
    }
  }
  return false;
}

export function isMeasureMirrorBarChart(dataView: DataView) {
  if (dataView.visualizationKey === DataViewVisualizationKey.CHART.MIRROR_BAR_CHART) {
    const allAttributeBundles = dataView.getAllAttributeBundles();
    const { MEASURE, DIMENSION } = CHART_BUNDLE_NAMES;
    return allAttributeBundles[DIMENSION]?.length === 1 && allAttributeBundles[MEASURE]?.length === 2;
  }
  return false;
}

export function getMapGeometryAttribute(dataViewId: DataViewId): WorkbookAttributeDto | undefined {
  const geometryActiveAttributeId: ActiveAttributeId = getDataViewById(getStore().getState(), dataViewId).attributes[
    MAP_BUNDLE_NAMES.GEOMETRY
  ]?.[0];
  return geometryActiveAttributeId ? getAttributeById(getStore().getState(), geometryActiveAttributeId) : undefined;
}

export function getGeometryAttributeActiveId(dataViewId: DataViewId): ActiveAttributeId | undefined {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  return dataView.attributes[MAP_BUNDLE_NAMES.GEOMETRY]?.[0];
}

/**
 * Checks if all bundle configurations are valid.
 *
 * @param attributeBundleDefinitions - bundle definitions
 * @param attributes - the DataView's attributes
 * @return - true if all bundle configurations are valid.
 */
export function hasValidBundlesConfig(
  attributeBundleDefinitions: Record<string, AttributeBundleDefinitionDto>,
  attributes: Record<AttributeBundleName, unknown[]>,
) {
  return Object.entries(getInvalidBundles(attributeBundleDefinitions, attributes)).length === 0;
}

/**
 * Gets the invalid bundles of the DataView.
 *
 * @param attributeBundleDefinitions - bundle definitions
 * @param attributes - the DataView's attributes
 * @return invalid bundles; key: bundle name,
 *   value: attribute count and limits
 */
function getInvalidBundles(
  attributeBundleDefinitions: Record<string, AttributeBundleDefinitionDto>,
  attributes: Record<AttributeBundleName, unknown[]>,
) {
  const countAndLimitsPerBundle = getBundleAttributeCountAndLimits(attributeBundleDefinitions, attributes);
  const result = {};
  return entries(countAndLimitsPerBundle)
    .filter(([, bundleConfig]) => !isValidBundle(bundleConfig))
    .reduce(
      (obj, [bundleName, bundleConfig]) => {
        obj[bundleName] = bundleConfig;
        return obj;
      },
      result as Record<AttributeBundleName, BundleAttributeCountAndLimits>,
    );
}

/**
 * Gets the attribute count and limits for the given bundles.
 *
 * @param attributeBundleDefinitions - bundle definitions
 * @param attributes - the DataView's attributes
 * @return attribute count and limits for the bundles
 */
export function getBundleAttributeCountAndLimits(
  attributeBundleDefinitions: Record<string, AttributeBundleDefinitionDto>,
  attributes: Record<AttributeBundleName, unknown[]>,
) {
  const result: Partial<Record<AttributeBundleName, BundleAttributeCountAndLimits>> = {};
  for (const bundleDefinition of Object.values(attributeBundleDefinitions)) {
    result[bundleDefinition.attributeBundleName] = getAttributeCountAndLimits(bundleDefinition, attributes);
  }
  return result as Record<AttributeBundleName, BundleAttributeCountAndLimits>;
}

/**
 * Gets the attribute count and limits for a single bundle.
 *
 * @param attributeBundleDefinition - bundle definition
 * @param attributes - the DataView's attributes
 * @return attribute count and limits for the bundle
 */
function getAttributeCountAndLimits(
  attributeBundleDefinition: AttributeBundleDefinitionDto,
  attributes: Record<AttributeBundleName, unknown[]>,
): BundleAttributeCountAndLimits {
  return {
    current: attributes[attributeBundleDefinition.attributeBundleName].length,
    min: attributeBundleDefinition.minNumberOfAttributes ?? 0,
    max: attributeBundleDefinition.maxNumberOfAttributes ?? Number.POSITIVE_INFINITY,
  };
}

/**
 * Checks if the number of attributes in the bundle is within the allowed limits.
 *
 * @param bundleAttributeCountAndLimits - attribute count and limits for the bundle
 * @return whether the attribute count of the bundle is within the allowed limits
 */
export function isValidBundle(bundleAttributeCountAndLimits: BundleAttributeCountAndLimits) {
  return (
    bundleAttributeCountAndLimits.current >= bundleAttributeCountAndLimits.min &&
    bundleAttributeCountAndLimits.current <= bundleAttributeCountAndLimits.max
  );
}

/**
 * Collects all attribute ids from all bundles of the given data view.
 *
 */
export function getAllAttributeIdsFromDataView(dataView: DataViewDto) {
  return Object.values(dataView.attributes).flat() as ActiveAttributeId[];
}
